import { UserSearch, BrainCircuit, Handshake, ShieldCheck } from "lucide-react";

const steps = [
  {
    icon: UserSearch,
    title: "Tell Us About You",
    desc: "Answer a 60-second questionnaire about your trading style, experience level, capital and goals. That's all we need.",
  },
  {
    icon: BrainCircuit,
    title: "AI Scans 200+ Secure Platforms",
    desc: "Our algorithm compares fees, tools, regulation, asset coverage and support quality — every trading platform in our network is licensed, regulated and security-audited.",
  },
  {
    icon: Handshake,
    title: "Get Your Perfect Match",
    desc: "You're connected directly with the trading platform that fits you best. Account activation is guided step by step. No pressure, no obligation.",
  },
];

const stats = [
  { value: "12,400+", label: "Traders matched" },
  { value: "200+", label: "Vetted platforms" },
  { value: "96.4%", label: "Match satisfaction" },
  { value: "60 sec", label: "Average match time" },
];

export const HowItWorks = () => (
  <section id="how-it-works" className="px-4 sm:px-6 py-16 scroll-mt-28 bg-white border-y border-gray-200">
    <div className="max-w-6xl mx-auto">
      <div className="max-w-2xl">
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600">How It Works</span>
        <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-gray-900 mt-3">
          Your Perfect Trading Platform, Found While You Grab a Coffee
        </h2>
        <p className="text-base md:text-lg text-gray-600 mt-4">
          Choosing the wrong trading platform costs traders thousands in fees and missed opportunities.
          AI Central connects you only to secure, fully legitimate and regulated platforms.
        </p>
      </div>

      <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-8">
        {steps.map((s, i) => (
          <div
            key={i}
            data-testid={`how-it-works-step-${i + 1}`}
            className="relative bg-gray-50 border border-gray-200 rounded-2xl p-8 hover:border-blue-300 hover:shadow-md transition-all duration-200"
          >
            <span className="absolute top-6 right-6 font-mono text-4xl font-bold text-gray-200">0{i + 1}</span>
            <span className="w-12 h-12 rounded-xl bg-blue-600 flex items-center justify-center shadow-lg shadow-blue-600/20">
              <s.icon className="w-6 h-6 text-white" />
            </span>
            <h3 className="font-heading text-xl font-bold text-gray-900 mt-6">{s.title}</h3>
            <p className="text-sm leading-relaxed text-gray-600 mt-3">{s.desc}</p>
          </div>
        ))}
      </div>

      <div data-testid="security-emphasis-strip" className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4 bg-emerald-50 border border-emerald-200 rounded-2xl px-8 py-6">
        <span className="w-11 h-11 shrink-0 rounded-xl bg-emerald-600 flex items-center justify-center">
          <ShieldCheck className="w-5 h-5 text-white" />
        </span>
        <p className="text-sm font-semibold text-emerald-900 leading-relaxed">
          100% of the trading platforms in our network are licensed, regulated and independently security-audited.
          Your funds and personal data are always protected.
        </p>
      </div>

      <div className="mt-14 grid grid-cols-2 md:grid-cols-4 border border-gray-200 rounded-2xl overflow-hidden divide-x divide-y md:divide-y-0 divide-gray-200">
        {stats.map((s, i) => (
          <div key={i} data-testid={`stat-${i}`} className="p-8 text-center bg-white">
            <p className="font-heading text-2xl md:text-3xl font-bold text-gray-900">{s.value}</p>
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mt-2">{s.label}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);
